import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
	Container,
	Paper,
	TextField,
	Button,
	Typography,
	Box,
	Alert,
	Grid,
	InputAdornment,
	Divider,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import i18next from "../lang";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { rootStore } from '../stores/RootStore';
import { isPasswordValid, PASSWORD_RULE_MESSAGE } from '../utils/passwordValidator';

interface ParentInfo {
	id: string;
	name: string;
	level: 'ADMIN' | 'CORP_PARTNER' | 'BRAND_MANAGER' | 'CLASSROOM_MANAGER';
	companyName?: string;
	brandName?: string;
	organizationName?: string;
}

interface ChildrenInfo {
	id: string;
	organizationName: string;
}

interface AncestorResponse {
	parent: ParentInfo;
	ancestors: ParentInfo[];
}

interface Address {
	postalCode: string;
	prefecture: string;
	city: string;
	streetAddress: string;
	building: string;
	aliasName: string;
}

interface ContactInfo {
	type: 'EMAIL' | 'PHONE' | 'FAX';
	value: string;
}

interface RegisterFormData {
	id: string;
	username: string;
	password: string;
	passwordConfirm: string;
	email: string;
	phone: string;
	fax: string;
	contacts: ContactInfo[];
	companyName: string;
	brandName: string;
	classroomName: string;
	parentId: string;
	addresses: Address[];
	organizationName: string;
	level: 'CORP_PARTNER' | 'BRAND_MANAGER' | 'CLASSROOM_MANAGER' | '';
	dueDate: number;
	paymentMonth: number;
	paymentDate: number;
}

interface SignUpFormProps {
	onSuccess?: () => void;
	onCancel?: () => void;
	isDialog?: boolean;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess, onCancel, isDialog = false }) => {
	const { authStore } = rootStore;
	const navigate = useNavigate();
	const [formData, setFormData] = useState<RegisterFormData>({
		id: '',
		username: '',
		password: '',
		passwordConfirm: '',
		email: '',
		phone: '',
		fax: '',
		contacts: [],
		companyName: '',
		brandName: '',
		classroomName: '',
		parentId: '',
		addresses: [{
			postalCode: '',
			prefecture: '',
			city: '',
			streetAddress: '',
			building: '',
			aliasName: ''
		}],
		organizationName: '',
		level: '',
		dueDate: 25,
		paymentMonth: 1,
		paymentDate: 0
	});
	const [error, setError] = useState<string>('');
	const [success, setSuccess] = useState<string>('');
	const [passwordError, setPasswordError] = useState<string>('');
	const [passwordConfirmError, setPasswordConfirmError] = useState<string>('');
	const [isIdVerified, setIsIdVerified] = useState<boolean>(false);
	const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
	const [isIdValid, setIsIdValid] = useState<string>('');
	const [isUsernameValid] = useState<string>('');
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [openTypeErrorDialog, setOpenTypeErrorDialog] = useState<boolean>(false);
	const [typeErrorMessage, setTypeErrorMessage] = useState<string>('');
	const [cropPartnerList, setCropPartnerList] = useState<ChildrenInfo[] | null>(null);
	const [brandManagerList, setBrandManagerList] = useState<ChildrenInfo[] | null>(null);
	const [cropPartner, setCropPartner] = useState<string>('');
	const [brandManager, setBrandManager] = useState<string>('');

	const handleChangeType = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (authStore.isAuthenticated) {
			if (authStore.user.level !== 'ADMIN') {
				switch (e.target.value) {
					case 'CORP_PARTNER':
						setTypeErrorMessage("auth.contact_tamai");
						setOpenTypeErrorDialog(true);
						return;
					case 'BRAND_MANAGER':
						if (authStore.user.level !== 'CORP_PARTNER') {
							setTypeErrorMessage("auth.login_with_corp_id");
							setOpenTypeErrorDialog(true);
							return;
						} else {
							break;
						}
				}
			}

			setCropPartnerList(null);
			setBrandManagerList(null);
			setCropPartner('');
			setBrandManager('');

			let isDirectChild = false;
			if (authStore.user.level === 'ADMIN' && e.target.value === 'CORP_PARTNER') {
				isDirectChild = true;
			} else if (authStore.user.level === 'CORP_PARTNER' && e.target.value === 'BRAND_MANAGER') {
				isDirectChild = true;
			} else if (authStore.user.level === 'BRAND_MANAGER' && e.target.value === 'CLASSROOM_MANAGER') {
				isDirectChild = true;
			}

			let newParentId = '';
			if (!isDirectChild) {
				if (e.target.value !== '') {
					if (authStore.user.level !== 'BRAND_MANAGER') {
						const response = await fetchChildrens(authStore.user.id);
						if (authStore.user.level === 'ADMIN') {
							setCropPartnerList(response.data);
						} else {
							setBrandManagerList(response.data);
						}
					}
				}
			} else {
				newParentId = authStore.user.id;
			}

			setFormData({
				...formData,
				level: e.target.value as 'CORP_PARTNER' | 'BRAND_MANAGER' | 'CLASSROOM_MANAGER' | '',
				parentId: newParentId
			});
		} else {
			switch (e.target.value) {
				case 'BRAND_MANAGER':
					setTypeErrorMessage("auth.login_with_corp_id");
					setOpenTypeErrorDialog(true);
					return;
				case 'CLASSROOM_MANAGER':
					setTypeErrorMessage("auth.login_with_brand_id");
					setOpenTypeErrorDialog(true);
					return;
				default:
					setTypeErrorMessage("auth.contact_tamai");
					setOpenTypeErrorDialog(true);
					return;
			}
		}
	}

	const handleChangeCropPartner = async (e: React.ChangeEvent<HTMLInputElement>) => {
		setBrandManagerList(null);
		setBrandManager('');
		setCropPartner(e.target.value);
		if (formData.level !== 'BRAND_MANAGER') {
			setFormData({
				...formData,
				parentId: ''
			});

			if (e.target.value !== '') {
				const response = await fetchChildrens(e.target.value);
				setBrandManagerList(response.data);
			}
		} else {
			setFormData({
				...formData,
				parentId: e.target.value
			});
		}
	}

	const handleChangeBrandManager = async (e: React.ChangeEvent<HTMLInputElement>) => {
		setBrandManager(e.target.value);
		setFormData({
			...formData,
			parentId: e.target.value
		});
	}

	const fetchChildrens = async (parentId: string) => {
		try {
			return await axios.get(`/api/v1/admin/users/${parentId}/childrens`);
		} catch (err: any) {
			setError(i18next.t('error.sub_id_search_failed'));
		}
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name } = e.target;
		setFormData({
			...formData,
			[name]: e.target.value
		});
		if (name === 'id') {
			setIsIdVerified(false);
			checkIdValid(e.target.value);
		} else if (name === 'email') {
			setIsEmailVerified(false);
		}
	};

	const handleChangeDate = (field: string, value: number) => {
		setFormData({
			...formData,
			[field]: value
		});
	};

	const handleAddressChange = (index: number, field: keyof Address, value: string) => {
		const newAddresses = [...formData.addresses];
		newAddresses[index] = {
			...newAddresses[index],
			[field]: value
		};
		setFormData({
			...formData,
			addresses: newAddresses
		});
	};

	const handlePostalCodeChange = (index: number, value: string) => {
		// 숫자와 하이픈만 허용
		const cleanedValue = value.replace(/[^\d-]/g, '');

		// 최대 8자리(하이픈 포함) 제한
		if (cleanedValue.length <= 8) {
			handleAddressChange(index, 'postalCode', cleanedValue);
		}
	};

	const isValidPostalCode = (code: string): boolean => {
		return /^\d{3}-?\d{4}$/.test(code);
	};

	const handlePostalCodeSearch = async (index: number) => {
		setError('');
		const address = formData.addresses[index];
		if (!address.postalCode) return;

		if (!isValidPostalCode(address.postalCode)) {
			setError(i18next.t('error.invalid_postal_code'));
			return;
		}

		// 우편번호 포맷팅 (7자리 숫자인 경우 하이픈 추가)
		let formattedPostalCode = address.postalCode;
		if (/^\d{7}$/.test(address.postalCode)) {
			formattedPostalCode = `${address.postalCode.slice(0, 3)}-${address.postalCode.slice(3)}`;
			handleAddressChange(index, 'postalCode', formattedPostalCode);
		}

		try {
			const cleanedZipcode = formattedPostalCode.replace(/-/g, '');
			const response = await axios.get(`/api/v1/auth/search-address?zipcode=${cleanedZipcode}`);

			if (response.data.status === 400) {
				setError(i18next.t('error.invalid_postal_code'));
				return;
			}

			if (response.data.results === null) {
				setError(i18next.t('error.address_not_found'));
				return;
			}

			const addressData = response.data.results[0];

			const newAddresses = [...formData.addresses];
			// 도도부현 (都道府県)
			newAddresses[index] = { ...newAddresses[index], 'prefecture': addressData.address1 };
			// 시구정촌 (市区町村)
			newAddresses[index] = { ...newAddresses[index], 'city': addressData.address2 };
			// 정목번지 (町域名)
			newAddresses[index] = { ...newAddresses[index], 'streetAddress': addressData.address3 };
			// 건물명이 있는 경우
			newAddresses[index] = { ...newAddresses[index], 'building': addressData.address4 ?? "" };

			setFormData({
				...formData,
				addresses: newAddresses
			});
		} catch (err) {
			setError(i18next.t('error.postal_code_search_error'));
		}
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError('');

		// 필수 필드 검증（空欄チェックを先に実行）
		const requiredFields: { [key: string]: string } = {
			id: i18next.t('error.enter_id'),
			email: i18next.t('error.enter_email'),
			password: i18next.t('error.enter_password')
		};

		for (const [field, message] of Object.entries(requiredFields)) {
			if (!formData[field as keyof RegisterFormData]) {
				setError(message);
				return;
			}
		}

		// パスワード形式チェック（空欄チェック通過後）
		if (!isPasswordValid(formData.password)) {
			setPasswordError(PASSWORD_RULE_MESSAGE);
			return;
		}

		// 패스워드 일치 여부 확인
		if (formData.password !== formData.passwordConfirm) {
			setPasswordConfirmError(i18next.t('error.password_mismatch'));
			return;
		}

		// 기본 주소 필수 필드 검증
		const mainAddress = formData.addresses[0];
		if (!mainAddress.postalCode || !mainAddress.prefecture || !mainAddress.city || !mainAddress.streetAddress) {
			setError(i18next.t('error.default_address_required'));
			return;
		}

		// 검증 상태 확인
		if (!formData.parentId) {
			setError(i18next.t('error.check_parent_id'));
			return;
		}
		if (!isIdVerified) {
			setError(i18next.t('error.check_id_duplicate'));
			return;
		}
			try {
			let { passwordConfirm, companyName, brandName, classroomName, ...submitData } = formData;
			let organizationName = "";
			switch (formData?.level) {
				case 'CORP_PARTNER':
					organizationName = companyName;
					break;
				case 'BRAND_MANAGER':
					organizationName = brandName;
					break;
				case 'CLASSROOM_MANAGER':
					organizationName = classroomName;
					break;
			}

			// 주소 정보 가공
			const addresses = formData.addresses.map((address, index) => ({
				...address,
				isDefault: index === 0,
				contactName: organizationName,
				contactPhone: '',
				contactFax: ''
			}));

			submitData = { ...submitData, organizationName, addresses };
			await axios.post('/api/v1/auth/register', submitData);

			if (authStore.isAuthenticated) {
				if (submitData.level === 'CORP_PARTNER') {
					navigate(`/admin/product-mappings/${submitData.id}`);
				}
				onSuccess && onSuccess();
			} else {
				setOpenDialog(true);
			}

		} catch (err: any) {
			let errorMessage = i18next.t('error.registration_error');

			if (err.response) {
				if (typeof err.response.data === 'string') {
					errorMessage = err.response.data;
				} else if (err.response.data?.message) {
					errorMessage = err.response.data.message;
				} else if (err.response.data?.error) {
					errorMessage = err.response.data.error;
				}
			}

			setError(errorMessage);
		}
	};

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));

		// パスワード形式チェック（passwordフィールドのみ）
		if (name === 'password') {
			if (value && !isPasswordValid(value)) {
				setPasswordError(PASSWORD_RULE_MESSAGE);
			} else {
				setPasswordError('');
			}
			// 確認欄との一致チェック
			if (formData.passwordConfirm && value !== formData.passwordConfirm) {
				setPasswordConfirmError(i18next.t('error.password_mismatch'));
			} else {
				setPasswordConfirmError('');
			}
		}
		// 確認欄の一致チェック
		if (name === 'passwordConfirm') {
			if (value && formData.password !== value) {
				setPasswordConfirmError(i18next.t('error.password_mismatch'));
			} else {
				setPasswordConfirmError('');
			}
		}
	};

	// const fetchParentInfo = async (parentId: string) => {
	// 	setError('');
	//
	// 	try {
	// 		const url = authStore.isAuthenticated ? `/api/v1/auth/subordinate/parent-info/${parentId}` : `/api/v1/auth/parent-info/${parentId}`;
	// 		const response = await axios.get<AncestorResponse>(url);
	//
	// 		const { parent, ancestors } = response.data;
	//
	// 		// if (parent.level === 'CLASSROOM_VIEWER') {
	// 		// 	setError(i18next.t('상위 ID 검증에 실패했습니다.'));
	// 		// 	setIsParentVerified(false);
	// 		// 	setParentInfo(null);
	// 		// 	return;
	// 		// }
	//
	// 		setParentInfo(parent);
	// 		setIsParentVerified(true);
	//
	// 		let updatedFormData = { ...formData };
	// 		let organizationName = '';
	//
	// 		updatedFormData.companyName = '';
	// 		updatedFormData.brandName = '';
	// 		updatedFormData.classroomName = '';
	//
	// 		const corpPartner = ancestors.find(a => a.level === 'CORP_PARTNER');
	// 		const brandManager = ancestors.find(a => a.level === 'BRAND_MANAGER');
	//
	// 		switch (parent.level) {
	// 			case 'ADMIN':
	// 				updatedFormData = {
	// 					...updatedFormData,
	// 					level: 'CORP_PARTNER',
	// 					brandName: '',
	// 					classroomName: ''
	// 				};
	// 				organizationName = updatedFormData.companyName;
	// 				break;
	// 			case 'CORP_PARTNER':
	// 				updatedFormData = {
	// 					...updatedFormData,
	// 					level: 'BRAND_MANAGER',
	// 					companyName: parent.organizationName,
	// 					classroomName: ''
	// 				};
	// 				organizationName = updatedFormData.brandName;
	// 				break;
	// 			case 'BRAND_MANAGER':
	// 				updatedFormData = {
	// 					...updatedFormData,
	// 					level: 'CLASSROOM_MANAGER',
	// 					companyName: corpPartner?.organizationName || '',
	// 					brandName: parent.organizationName
	// 				};
	// 				organizationName = updatedFormData.classroomName;
	// 				break;
	// 		}
	//
	// 		updatedFormData.organizationName = organizationName;
	// 		setFormData(updatedFormData);
	// 	} catch (err: any) {
	// 		setError(i18next.t('상위 ID 검증에 실패했습니다.'));
	// 		setIsParentVerified(false);
	// 		setParentInfo(null);
	// 	}
	// };

	// const handleParentSearch = async (e: React.MouseEvent<HTMLButtonElement>) => {
	// 	e.preventDefault();
	// 	setError('');
	//
	// 	if (!formData.parentId) {
	// 		setError(i18next.t('상위 ID를 입력해주세요.'));
	// 		return;
	// 	}
	//
	// 	try {
	// 		await fetchParentInfo(formData.parentId);
	// 	} catch (err: any) {
	// 		setError(i18next.t('상위 ID 검색에 실패했습니다.'));
	// 	}
	// };

	// const handleParentIdKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
	// 	if (e.key === 'Enter') {
	// 		e.preventDefault();
	// 		if (formData.parentId) {
	// 			fetchParentInfo(formData.parentId);
	// 		}
	// 	}
	// };

	const isFieldDisabled = (fieldName: string): boolean => {
		switch (fieldName) {
			case 'companyName':
				return formData.level !== 'CORP_PARTNER';
			case 'brandName':
				return formData.level !== 'BRAND_MANAGER';
			case 'classroomName':
				return formData.level !== 'CLASSROOM_MANAGER';
			default:
				return true;
		}
	};

	const checkEmailDuplicate = async (email: string) => {
		setError('');
		setSuccess('');

		if (!email) {
			setError(i18next.t('error.enter_email'));
			return;
		}

		try {
			await axios.get(`/api/v1/auth/check-email/${encodeURIComponent(email)}`);
			setSuccess(i18next.t('auth.email_available'));
			setIsEmailVerified(true);
		} catch (err: any) {
			setError(i18next.t('auth.email_already_registered'));
			setIsEmailVerified(false);
		}
	};

	const handleEmailKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			checkEmailDuplicate(formData.email);
		}
	};

	const checkIdDuplicate = async () => {
		setError('');
		setSuccess('');

		if (!formData.id) {
			setError(i18next.t('error.enter_id'));
			return;
		}

		try {
			await axios.get(`/api/v1/auth/check-id/${encodeURIComponent(formData.id)}`);
			setSuccess(i18next.t('auth.id_available'));
			setIsIdVerified(true);
		} catch (err: any) {
			setError(i18next.t('auth.id_already_registered'));
			setIsIdVerified(false);
		}
	};

	const handleIdKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			checkIdDuplicate();
		}
	};

	const checkIdValid = (id: string) => {
		if (id !== '') {
			const regex = /^[a-zA-Z0-9]{1,10}$/;
			if (!regex.test(id)) {
				setIsIdValid(i18next.t('auth.alphanumeric_max10'));
			} else {
				setIsIdValid('');
			}
		} else {
			setIsIdValid('');
		}
	};

	const isSubmitDisabled = (): boolean => {
		// 기존 검증 상태 확인
		const verificationChecks = !formData.parentId || !isIdVerified;

		// 필수 입력 필드 확인
		const mainAddress = formData.addresses[0];
		let requiredFieldChecks = !formData.password ||
			!(formData.companyName || formData.brandName || formData.classroomName) ||
			!mainAddress.postalCode ||
			!mainAddress.prefecture ||
			!mainAddress.city ||
			!mainAddress.streetAddress;

		if (formData.level) {
			if (formData.level === 'CORP_PARTNER') {
				if (!formData.companyName) requiredFieldChecks = true;
			}
			if (formData.level === 'BRAND_MANAGER') {
				if (!formData.brandName) requiredFieldChecks = true;
			}
			if (formData.level === 'CLASSROOM_MANAGER') {
				if (!formData.classroomName) requiredFieldChecks = true;
			}
		}

		// パスワード形式・一致チェック
		const passwordInvalid = !isPasswordValid(formData.password);
		const passwordMismatch = formData.password !== formData.passwordConfirm;

		// 모든 조건이 만족되어야 버튼이 활성화됨
		return verificationChecks || requiredFieldChecks || passwordInvalid || passwordMismatch;
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		if (onSuccess) {
			onSuccess();
		} else {
			navigate('/login');
		}
	};

	const handleAddAddress = () => {
		setFormData({
			...formData,
			addresses: [...formData.addresses, {
				postalCode: '',
				prefecture: '',
				city: '',
				streetAddress: '',
				building: '',
				aliasName: ''
			}]
		});
	};

	const handleRemoveAddress = (index: number) => {
		// 첫 번째 주소는 삭제 불가
		if (index === 0) return;

		// 최소 하나의 주소는 유지
		if (formData.addresses.length > 1) {
			const newAddresses = formData.addresses.filter((_, i) => i !== index);
			setFormData({
				...formData,
				addresses: newAddresses
			});
		}
	};

	let hierarchy = formData.companyName ?? "";
	hierarchy += formData.brandName ? " > " + formData.brandName : "";
	hierarchy += formData.classroomName ? " > " + formData.classroomName : "";

	return (
		<Container maxWidth="sm" sx={{ py: isDialog ? 2 : 4 }}>
			<Paper elevation={isDialog ? 0 : 3} sx={{ p: 4 }}>
				<Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 750 }}>
					{i18next.t('auth.new_registration')}
				</Typography>

				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
				{success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

				<Box component="form" onSubmit={handleSubmit}>
					<FormControl fullWidth margin="dense" size="small">
						<InputLabel>{i18next.t('auth.account_type')}</InputLabel>
						<Select
							value={formData.level}
							onChange={handleChangeType}
							label={i18next.t('auth.account_type')}
						>
							<MenuItem value="">
								<em>{i18next.t('auth.select_account_type')}</em>
							</MenuItem>
							{authStore.user.level === 'ADMIN' &&
								<MenuItem value="CORP_PARTNER">{i18next.t('enum.corp_partner')}</MenuItem>
							}
							{(authStore.user.level === 'ADMIN' || authStore.user.level === 'CORP_PARTNER') &&
								<MenuItem value="BRAND_MANAGER">{i18next.t('enum.brand_manager')}</MenuItem>
							}
							{(authStore.user.level === 'ADMIN' || authStore.user.level === 'CORP_PARTNER' || authStore.user.level === 'BRAND_MANAGER') &&
								<MenuItem value="CLASSROOM_MANAGER">{i18next.t('enum.classroom_manager')}</MenuItem>
							}
						</Select>
					</FormControl>

					{/*<TextField*/}
					{/*	fullWidth*/}
					{/*	required*/}
					{/*	margin="dense"*/}
					{/*	size="small"*/}
					{/*	label={i18next.t('상위 ID (필수)')}*/}
					{/*	name="parentId"*/}
					{/*	value={formData.parentId}*/}
					{/*	onChange={handleChange}*/}
					{/*	onKeyPress={handleParentIdKeyPress}*/}
					{/*	helperText={!isParentVerified ? i18next.t('먼저 상위 ID를 입력해주세요') : ''}*/}
					{/*	InputProps={{*/}
					{/*		endAdornment: (*/}
					{/*			<InputAdornment position="end">*/}
					{/*				<Button*/}
					{/*					size="small"*/}
					{/*					onClick={handleParentSearch}*/}
					{/*					sx={{*/}
					{/*						textTransform: 'none',*/}
					{/*						minWidth: 'auto',*/}
					{/*						color: 'primary.main'*/}
					{/*					}}*/}
					{/*					startIcon={<SearchIcon fontSize="small" />}*/}
					{/*				>*/}
					{/*					{i18next.t('상위 검색')}*/}
					{/*				</Button>*/}
					{/*			</InputAdornment>*/}
					{/*		),*/}
					{/*	}}*/}
					{/*/>*/}

					{cropPartnerList &&
						<FormControl fullWidth margin="dense" size="small">
							<InputLabel>{i18next.t('user.corp_admin')}</InputLabel>
							<Select
								value={cropPartner}
								onChange={handleChangeCropPartner}
								label={i18next.t('user.corp_admin')}
							>
								<MenuItem value="">
									<em>{i18next.t('auth.select_company_admin')}</em>
								</MenuItem>
								{cropPartnerList.map(({ id, organizationName }) => (
									<MenuItem key={id} value={id}>
										{`${id} (${organizationName})`}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					}

					{brandManagerList &&
						<FormControl fullWidth margin="dense" size="small">
							<InputLabel>{i18next.t('user.brand_admin')}</InputLabel>
							<Select
								value={brandManager}
								onChange={handleChangeBrandManager}
								label={i18next.t('user.brand_admin')}
							>
								<MenuItem value="">
									<em>{i18next.t('auth.select_brand_admin')}</em>
								</MenuItem>
								{brandManagerList.map(({ id, organizationName }) => (
									<MenuItem key={id} value={id}>
										{`${id} (${organizationName})`}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					}


					{/*{parentInfo && (*/}
					{/*	<Alert severity="info" sx={{ mt: 1, mb: 2 }}>*/}
					{/*		{parentInfo.level === 'ADMIN' && (*/}
					{/*			formData.companyName === "" ? (*/}
					{/*				i18next.t("회사명 입력란")*/}
					{/*			) : (*/}
					{/*				<span>*/}
					{/*					<strong>{hierarchy}</strong>*/}
					{/*				</span>*/}
					{/*			)*/}
					{/*		)}*/}
					{/*		{parentInfo.level === 'CORP_PARTNER' && (*/}
					{/*			formData.brandName === "" ? (*/}
					{/*				i18next.t("{{hierarchy}}의 브랜드명 입력란", {hierarchy: hierarchy})*/}
					{/*			) : (*/}
					{/*				<span>*/}
					{/*					<strong>{hierarchy}</strong>*/}
					{/*				</span>*/}
					{/*			)*/}
					{/*		)}*/}
					{/*		{parentInfo.level === 'BRAND_MANAGER' && (*/}
					{/*			formData.classroomName === "" ? (*/}
					{/*				i18next.t("{{hierarchy}}의 교실명 입력란", {hierarchy: hierarchy})*/}
					{/*			) : (*/}
					{/*				<span>*/}
					{/*					<strong>{hierarchy}</strong>*/}
					{/*				</span>*/}
					{/*			)*/}
					{/*		)}*/}
					{/*		{parentInfo.level === 'CLASSROOM_MANAGER' && (*/}
					{/*				<strong>{hierarchy}</strong>*/}
					{/*		)}*/}
					{/*	</Alert>*/}
					{/*)}*/}

					{(formData?.level === 'CORP_PARTNER') && (
						<TextField
							fullWidth
							required
							margin="dense"
							size="small"
							label={i18next.t('user.company_name')}
							name="companyName"
							value={formData.companyName}
							onChange={handleChange}
							disabled={isFieldDisabled('companyName')}
						/>
					)}

					{(formData?.level === 'BRAND_MANAGER') && (
						<TextField
							fullWidth
							required
							margin="dense"
							size="small"
							label={i18next.t('user.brand_name')}
							name="brandName"
							value={formData.brandName}
							onChange={handleChange}
							disabled={isFieldDisabled('brandName')}
						/>
					)}

					{(formData?.level === 'CLASSROOM_MANAGER') && (
						<TextField
							fullWidth
							required
							margin="dense"
							size="small"
							label={i18next.t('user.classroom_name')}
							name="classroomName"
							value={formData.classroomName}
							onChange={handleChange}
							disabled={isFieldDisabled('classroomName')}
						/>
					)}

					<Divider sx={{ my: 2, fontWeight: 750 }}>{i18next.t('auth.account_info')}</Divider>

					<TextField
						fullWidth
						required
						margin="dense"
						size="small"
						label={i18next.t('common.id')}
						name="id"
						value={formData.id}
						onChange={handleChange}
						onKeyPress={handleIdKeyPress}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<Button
										size="small"
										onClick={checkIdDuplicate}
										disabled={isIdValid !== ''}
										sx={{
											textTransform: 'none',
											minWidth: 'auto',
											color: 'primary.main'
										}}
										startIcon={<SearchIcon fontSize="small" />}
									>
										{i18next.t('common.check_duplicate')}
									</Button>
								</InputAdornment>
							),
						}}
						error={formData.id !== '' && (!isIdVerified || isIdValid !== '')}
						helperText={formData.id === '' ? '' : isIdValid !== '' ? isIdValid : !isIdVerified ? i18next.t('error.check_id_duplicate') : ''}
					/>

					<Grid container spacing={2}>
						<Grid item xs={6}>
							<TextField
								fullWidth
								required
								margin="dense"
								size="small"
								label={i18next.t('auth.password')}
								name="password"
								type="password"
								placeholder="半角英数字8〜20文字"
								value={formData.password}
								onChange={handlePasswordChange}
								error={!!passwordError}
								helperText={passwordError || '半角英数字8〜20文字'}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								fullWidth
								required
								margin="dense"
								size="small"
								label={i18next.t('auth.password_confirm')}
								name="passwordConfirm"
								type="password"
								value={formData.passwordConfirm}
								onChange={handlePasswordChange}
								error={!!passwordConfirmError}
								helperText={passwordConfirmError}
							/>
						</Grid>
					</Grid>

					<TextField
						fullWidth
						required
						margin="dense"
						size="small"
						label={i18next.t('contact.email_label')}
						name="email"
						type="email"
						value={formData.email}
						onChange={handleChange}
					/>

					<Grid container spacing={2}>
						<Grid item xs={6}>
							<TextField
								fullWidth
								margin="dense"
								size="small"
								label={i18next.t('contact.phone')}
								name="phone"
								value={formData.phone}
								onChange={handleChange}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								fullWidth
								margin="dense"
								size="small"
								label={i18next.t('contact.fax')}
								name="fax"
								value={formData.fax}
								onChange={handleChange}
							/>
						</Grid>
					</Grid>

					{formData.level === 'CORP_PARTNER' &&
						<Grid container spacing={2} style={{paddingTop: 16}}>
							<Grid item xs={6}>
								<FormControl fullWidth margin="dense" size="small">
									<InputLabel>{i18next.t('user.due_date')}</InputLabel>
									<Select
										value={formData.dueDate}
										label={i18next.t('user.due_date') + '\u2004'}
										onChange={(e) => handleChangeDate('dueDate', Number(e.target.value))}
										sx={{
											width: '100%',
											fontSize: '0.8rem'
										}}
										MenuProps={{
											PaperProps: {
												sx: {
													maxHeight: 300,
													overflowY: 'auto'
												}
											}
										}}
									>
										{Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
											<MenuItem key={day} value={day} sx={{ fontSize: '0.8rem' }}>
												{day}{i18next.t('common.day')}
											</MenuItem>
										))}
										<MenuItem key={0} value={0} sx={{ fontSize: '0.8rem' }}>
											{i18next.t('user.end_of_month')}
										</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={6}>
								<Grid container spacing={2} alignItems="center">
									<Grid item xs={6}>
										<FormControl fullWidth margin="dense" size="small">
											<InputLabel>{i18next.t('user.payment_month')}</InputLabel>
											<Select
												value={formData.paymentMonth}
												label={i18next.t('user.payment_month') + '\u2004'}
												onChange={(e) => handleChangeDate('paymentMonth', Number(e.target.value))}
												sx={{
													width: '100%',
													fontSize: '0.8rem'
												}}
											>
												<MenuItem value={1} sx={{ fontSize: '0.8rem' }}>
													{i18next.t('user.next_month')}
												</MenuItem>
												<MenuItem value={2} sx={{ fontSize: '0.8rem' }}>
													{i18next.t('user.month_after_next')}
												</MenuItem>
											</Select>
										</FormControl>
									</Grid>
									<Grid item xs={6}>
										<FormControl fullWidth margin="dense" size="small">
											<InputLabel>{i18next.t('user.payment_date')}</InputLabel>
											<Select
												value={formData.paymentDate}
												label={i18next.t('user.payment_date') + '\u2004'}
												onChange={(e) => handleChangeDate('paymentDate', Number(e.target.value))}
												sx={{
													width: '100%',
													fontSize: '0.8rem'
												}}
												MenuProps={{
													PaperProps: {
														sx: {
															maxHeight: 300,
															overflowY: 'auto'
														}
													}
												}}
											>
												{Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
													<MenuItem key={day} value={day} sx={{ fontSize: '0.8rem' }}>
														{day}{i18next.t('common.day')}
													</MenuItem>
												))}
												<MenuItem key={0} value={0} sx={{ fontSize: '0.8rem' }}>
													{i18next.t('user.end_of_month')}
												</MenuItem>
											</Select>
										</FormControl>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					}

					<Divider sx={{ my: 2, fontWeight: 750 }}>{i18next.t('contact.additional_contact')}</Divider>

					<Box sx={{ mb: 2 }}>
						{formData.contacts.map((contact, index) => (
							<Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
								<FormControl sx={{ width: '140px', mr: 1 }}>
									<InputLabel size="small" sx={{ fontSize: '0.7rem' }}>{i18next.t('common.type')}</InputLabel>
									<Select
										sx={{ fontSize: '0.7rem', height: '40px' }}
										size="small"
										value={contact.type}
										label={i18next.t('common.type')}
										onChange={(e) => {
											const newContacts = [...formData.contacts];
											newContacts[index] = {
												...newContacts[index],
												type: e.target.value as 'EMAIL' | 'PHONE' | 'FAX'
											};
											setFormData({ ...formData, contacts: newContacts });
										}}
									>
										<MenuItem value="EMAIL" sx={{ fontSize: '0.7rem', height: '40px' }}>{i18next.t('contact.email')}</MenuItem>
										<MenuItem value="PHONE" sx={{ fontSize: '0.7rem', height: '40px' }}>{i18next.t('contact.phone')}</MenuItem>
										<MenuItem value="FAX" sx={{ fontSize: '0.7rem', height: '40px' }}>{i18next.t('contact.fax')}</MenuItem>
									</Select>
								</FormControl>
								<TextField
									size="small"
									fullWidth
									value={contact.value}
									onChange={(e) => {
										const newContacts = [...formData.contacts];
										newContacts[index] = { ...newContacts[index], value: e.target.value };
										setFormData({ ...formData, contacts: newContacts });
									}}
									placeholder={
										contact.type === 'EMAIL'
											? i18next.t('contact.enter_additional_email')
											: contact.type === 'PHONE'
												? i18next.t('contact.enter_additional_phone')
												: i18next.t('contact.enter_additional_fax')
									}
								/>
								<IconButton
									onClick={() => {
										const newContacts = [...formData.contacts];
										newContacts.splice(index, 1);
										setFormData({ ...formData, contacts: newContacts });
									}}
									color="error"
									sx={{ ml: 1 }}
								>
									<DeleteIcon />
								</IconButton>
							</Box>
						))}
						<Box sx={{ mt: 1 }}>
							<Button
								size="small"
								startIcon={<AddIcon />}
								onClick={() => {
									setFormData({
										...formData,
										contacts: [
											...formData.contacts,
											{ type: 'EMAIL', value: '' }
										]
									});
								}}
							>
								{i18next.t('contact.add_contact')}
							</Button>
						</Box>
					</Box>

					<Divider sx={{ my: 2, fontWeight: 750 }}>{i18next.t('address.address_info')}</Divider>

					{formData.addresses.map((address, index) => (
						<Box key={index} sx={{ mb: index < formData.addresses.length - 1 ? 3 : 0 }}>
							{index > 0 && (
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
									<Typography variant="subtitle1">
										{`${i18next.t('address.additional_address')} #${index}`}
									</Typography>
									<Button
										size="small"
										color="error"
										onClick={() => handleRemoveAddress(index)}
										startIcon={<DeleteIcon />}
									>
										{i18next.t('common.delete')}
									</Button>
								</Box>
							)}

							<Grid container spacing={2}>
								<Grid item xs={12}>
									<TextField
										fullWidth
										required={index === 0}
										size="small"
										label={i18next.t('address.postal_code')}
										value={address.postalCode}
										onChange={(e) => handlePostalCodeChange(index, e.target.value)}
										placeholder={i18next.t('address.postal_code_example')}
										helperText={i18next.t('address.hyphen_optional')}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<Button
														onClick={() => handlePostalCodeSearch(index)}
														startIcon={<SearchIcon />}
														size="small"
														disabled={!isValidPostalCode(address.postalCode)}
													>
														{i18next.t('address.search_address')}
													</Button>
												</InputAdornment>
											),
										}}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										fullWidth
										required={index === 0}
										size="small"
										label={i18next.t('address.prefecture')}
										value={address.prefecture}
										onChange={(e) => handleAddressChange(index, 'prefecture', e.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										fullWidth
										required={index === 0}
										size="small"
										label={i18next.t('address.city')}
										value={address.city}
										onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										fullWidth
										required={index === 0}
										size="small"
										label={i18next.t('address.detail_address')}
										value={address.streetAddress}
										onChange={(e) => handleAddressChange(index, 'streetAddress', e.target.value)}
										helperText={i18next.t('address.building_hint')}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										fullWidth
										size="small"
										label={i18next.t('address.building_name')}
										value={address.building}
										onChange={(e) => handleAddressChange(index, 'building', e.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										fullWidth
										size="small"
										label={i18next.t('address.alias')}
										value={address.aliasName}
										onChange={(e) => handleAddressChange(index, 'aliasName', e.target.value)}
									/>
								</Grid>
							</Grid>
						</Box>
					))}

					<Button
						fullWidth
						variant="outlined"
						startIcon={<AddIcon />}
						onClick={handleAddAddress}
						sx={{ mt: 2 }}
					>
						{i18next.t('address.add_new_address')}
					</Button>

					<Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
						{isDialog && (
							<Button
								variant="outlined"
								fullWidth
								onClick={onCancel}
								sx={{ fontSize: '0.8rem' }}
							>
								{i18next.t('common.cancel')}
							</Button>
						)}
						<Button
							type="submit"
							variant="contained"
							fullWidth
							disabled={isSubmitDisabled()}
							sx={{ fontSize: '0.8rem' }}
						>
							{i18next.t('auth.register')}
						</Button>
					</Box>
				</Box>
			</Paper>

			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					{i18next.t('auth.registration_complete')}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{i18next.t('auth.registration_complete_message')}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog} autoFocus>
						{i18next.t('common.confirm')}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openTypeErrorDialog}
				onClose={() => setOpenTypeErrorDialog(false)}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					{i18next.t('auth.account_registration_unavailable')}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{i18next.t(typeErrorMessage)}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenTypeErrorDialog(false)} autoFocus>
						{i18next.t('common.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
};

export default SignUpForm;